import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Fade
} from '@mui/material';
import { Business, LocationOn } from '@mui/icons-material';
import { apiService } from '@services/api';
import { LANDING_CONTAINER_MAX_WIDTH, landingSectionSpacing } from '@/constants/landingLayout';

interface TenantInfo {
  tenantId: string;
  companyName: string;
  logo: string | null;
  city: string;
  state: string;
  website: string | null;
}

const ClientLogosSection: React.FC = () => {
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTenants = async () => {
      setLoading(true);
      try {
        // Force update on first load to bypass cache
        const response = await apiService.getPublicTenants(true);
        if (response.data && response.data.success) {
          const tenantsData = response.data.data || [];
          // Filtrar apenas tenants com logo válido
          const validTenants = tenantsData.filter((t: TenantInfo) => 
            t.logo && t.logo.trim() !== '' && t.logo.startsWith('data:')
          );
          setTenants(validTenants);
        } else {
          setTenants([]);
        }
      } catch (error: unknown) {
        setTenants([]);
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          ...landingSectionSpacing,
          bgcolor: 'background.default',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH}>
          <Typography variant="body2" color="text.secondary" align="center">
            Carregando empresas...
          </Typography>
        </Container>
      </Box>
    );
  }

  if (tenants.length === 0) {
    return null; // Não mostrar seção se não houver tenants com logo
  }

  return (
    <Box
      sx={{
        ...landingSectionSpacing,
        bgcolor: 'background.default',
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Empresas que Confiam na VOVER
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: '500px', mx: 'auto' }}
          >
            Gestores e profissionais escolhem a VOVER para gestão de demandas
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: { xs: 3, md: 4 }
          }}
        >
          {tenants.map((tenant, index) => (
            <Fade in={true} timeout={1000} key={tenant.tenantId} style={{ transitionDelay: `${index * 100}ms` }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  width: { xs: 160, md: 200 },
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    '& .logo-container': {
                      boxShadow: 4
                    }
                  }
                }}
              >
                <Box
                  component={tenant.website ? 'a' : 'div'}
                  href={tenant.website || undefined}
                  target={tenant.website ? '_blank' : undefined}
                  rel={tenant.website ? 'noopener noreferrer' : undefined}
                  className="logo-container"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 140, md: 180 },
                    height: { xs: 90, md: 120 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: { xs: 0.75, md: 1 },
                    transition: 'all 0.3s',
                    overflow: 'hidden',
                    cursor: tenant.website ? 'pointer' : 'default',
                    textDecoration: 'none',
                    '&:hover': tenant.website ? {
                      borderColor: 'primary.main',
                      boxShadow: 4
                    } : {}
                  }}
                >
                  {tenant.logo && tenant.logo.trim() !== '' ? (
                    <Box
                      component="img"
                      src={tenant.logo}
                      alt={tenant.companyName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <Business sx={{ fontSize: { xs: 40, md: 50 }, color: 'text.secondary' }} />
                  )}
                </Box>
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      fontSize: { xs: '0.7rem', md: '0.75rem' }
                    }}
                  >
                    <LocationOn sx={{ fontSize: { xs: 12, md: 14 } }} />
                    {tenant.city}, {tenant.state}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ClientLogosSection;

